import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { OfflineError } from "@/utils/errors";
import { useProjectsStore } from "@/store/modules/projects";

const { innerFetch, innerPartialUpdate, innerBulkCreate, innerBulkDelete } =
  crudGenerator(db.models.SampleFlask);

export const useSampleFlasksStore = defineStore("sampleFlasks", {
  state: () => ({
    sampleFlasksList: [],
    sampleFlaskIsLoading: false,
    sampleFlaskIsFetching: false,
    sampleFlaskForCurrentInstallations: [],
  }),
  actions: {
    async fetchSampleFlasks(projectUuid) {
      const showSkeleton = !this.sampleFlasksList.length;
      return innerFetch(
        { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.sampleFlaskIsFetching = isLoading;
          if (showSkeleton) this.sampleFlaskIsLoading = isLoading;
        }
      );
    },

    async getOrderFormSampleFlasks(orderFormUuid) {
      while (this.sampleFlaskIsFetching)
        await new Promise((r) => setTimeout(r, 200));
      const orderForm = await db.orderForms.get({ uuid: orderFormUuid });
      if (!orderForm?.sampleFlasks?.length) return [];
      const flaskUuids = orderForm.sampleFlasks.map((sf) => sf.uuid);
      const flasks = await db.sampleFlasks
        .where("uuid")
        .anyOf(flaskUuids)
        .toArray();
      for (const flask of flasks) {
        await flask.setRelationships();
        const flaskData = orderForm.sampleFlasks.find(
          (sf) => sf.uuid === flask.uuid
        );
        flask.priority = flaskData?.priority;
      }
      return flasks;
    },

    async getSampleFlasksByInstallations(installationUuids) {
      while (this.sampleFlaskIsFetching)
        await new Promise((r) => setTimeout(r, 200));
      const sampleFlaskList = await db.sampleFlasks
        .where("installation")
        .anyOf(installationUuids)
        .toArray();
      for (const sampleFlask of sampleFlaskList) {
        await sampleFlask.setRelationships();
      }
      this.sampleFlaskForCurrentInstallations = [...sampleFlaskList];
      return sampleFlaskList;
    },

    async setSampleFlasks(installationUuid) {
      while (this.sampleFlaskIsFetching)
        await new Promise((r) => setTimeout(r, 200));
      const showSkeleton = !this.sampleFlasksList.length;
      this.sampleFlaskIsFetching = true;
      if (showSkeleton) this.sampleFlaskIsLoading = true;
      try {
        const sampleFlaskList = await db.sampleFlasks
          .where("installation")
          .equals(installationUuid)
          .toArray();
        for (const sampleFlask of sampleFlaskList) {
          await sampleFlask.setRelationships();
          for (const sample of sampleFlask.sampleObjects) {
            await sample.setRelationships();
          }
        }
        this.sampleFlasksList = sampleFlaskList;

        return sampleFlaskList;
      } finally {
        this.sampleFlaskIsFetching = false;
        if (showSkeleton) this.sampleFlaskIsLoading = false;
      }
    },

    async updateSampleFlask(form) {
      this.sampleFlaskIsFetching = true;
      this.sampleFlaskIsLoading = true;
      try {
        const sampleFlask = await innerPartialUpdate(
          form,
          useProjectsStore().isOfflineState
        );
        this.replaceSampleFlask(sampleFlask);

        return sampleFlask;
      } finally {
        this.sampleFlaskIsFetching = false;
        this.sampleFlaskIsLoading = false;
      }
    },

    async updateSampleFlasks(forms) {
      this.sampleFlaskIsFetching = true;
      this.sampleFlaskIsLoading = true;
      try {
        const isOfflineState = useProjectsStore().isOfflineState;
        const results = await Promise.allSettled(
          forms.map((form) => innerPartialUpdate(form, isOfflineState))
        );
        const failedUuids = [];
        results.forEach((result, index) => {
          if (result.status === "fulfilled")
            this.replaceSampleFlask(result.value);
          else failedUuids.push(forms[index].uuid);
        });
        const failure = results.find(({ status }) => status === "rejected");
        return { failedUuids, error: failure?.reason };
      } finally {
        this.sampleFlaskIsFetching = false;
        this.sampleFlaskIsLoading = false;
      }
    },

    async bulkCreateSampleFlasks(form) {
      return innerBulkCreate(form, useProjectsStore().isOfflineState);
    },

    async deleteInstallationSampleFlasks(installationUuid) {
      if (isNetworkUnavailable()) {
        throw new OfflineError();
      }
      const projectsStore = useProjectsStore();
      const uuids = await db.sampleFlasks
        .where("installation")
        .equals(installationUuid)
        .primaryKeys();
      await innerBulkDelete(
        uuids,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid,
        true
      );
      this.sampleFlasksList = [];
    },

    removeSampleFlaskToCurrentInstallations(installationUuid) {
      this.sampleFlaskForCurrentInstallations =
        this.sampleFlaskForCurrentInstallations.filter(
          ({ installation }) => installation !== installationUuid
        );
    },

    addSampleFlaskToCurrentInstallations(sampleFlaskList) {
      const toAdd = sampleFlaskList.filter(
        (sample) =>
          !this.sampleFlaskForCurrentInstallations.some(
            ({ uuid }) => uuid === sample.uuid
          )
      );
      this.sampleFlaskForCurrentInstallations = [
        ...this.sampleFlaskForCurrentInstallations,
        ...toAdd,
      ];
    },

    replaceSampleFlask(sampleFlask) {
      const index = this.sampleFlasksList.findIndex(
        ({ uuid }) => sampleFlask.uuid === uuid
      );

      if (index >= 0) {
        this.sampleFlasksList[index] = sampleFlask;
        this.sampleFlasksList = [...this.sampleFlasksList];
      }

      const currentIndex = this.sampleFlaskForCurrentInstallations.findIndex(
        ({ uuid }) => sampleFlask.uuid === uuid
      );

      if (currentIndex >= 0) {
        this.sampleFlaskForCurrentInstallations[currentIndex] = sampleFlask;
        this.sampleFlaskForCurrentInstallations = [
          ...this.sampleFlaskForCurrentInstallations,
        ];
      }
    },
  },
});
